/* eslint-disable max-len */
import * as React from 'react';
import { CalendarDays, Clock } from 'lucide-react';
import type { DateRange, Matcher } from 'react-day-picker';

import { Button } from './button';
import { Calendar, CalendarDayButton } from './calendar';
import { Label } from './label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './popover';

/**
 * Props for the CustomDatePicker component
 */
export interface CustomDatePickerProps {
  /** Current date value in YYYY-MM-DD format (single mode) */
  value?: string

  /** Callback fired when date changes (single mode) */
  onChange?: (date: string) => void

  /** Selection mode - defaults to 'single' for backward compatibility */
  mode?: 'single' | 'range'

  /** Range value when mode is 'range' - replaces value prop */
  rangeValue?: { from?: string; to?: string }

  /** Callback for range changes when mode is 'range' - replaces onChange */
  onRangeChange?: (range: { from?: string; to?: string }) => void

  /** Dates to disable in the calendar */
  disabled?: Matcher | Matcher[]

  /**
   * Minimum selectable date (inclusive)
   * Applies to both single and range modes
   * Format: YYYY-MM-DD
   */
  minDate?: string

  /**
   * Maximum selectable date (inclusive)
   * Applies to both single and range modes
   * Format: YYYY-MM-DD
   */
  maxDate?: string

  /**
   * Array of valid dates that can be selected
   * Only applies to single mode (not range mode)
   * All other dates will be disabled
   * Can be combined with minDate/maxDate for additional constraints
   * Format: Array of YYYY-MM-DD strings
   */
  selectableDates?: string[]

  /** Placeholder text when no date is selected */
  placeholder?: string

  /** Placeholder text for range mode */
  rangePlaceholder?: string

  /** Additional CSS classes for the container */
  className?: string

  /** Additional CSS classes for the button/input element */
  buttonClassName?: string

  /**
   * Restrict selection to a specific day of the week
   * 0 = Sunday, 1 = Monday, ..., 6 = Saturday
   */
  restrictToDayOfWeek?: 0 | 1 | 2 | 3 | 4 | 5 | 6

  /**
   * Allow manual input of dates
   * Defaults to true unless restrictToDayOfWeek is set
   */
  allowManualInput?: boolean

  /** Callback fired when validation state changes */
  onValidationChange?: (isValid: boolean, errorMessage?: string) => void

  /** Show validation errors inline */
  showValidationError?: boolean

  /** Custom error message (overrides all validation errors) */
  errorMessage?: string

  /** Custom error message for required validation only */
  requiredErrorMessage?: string

  /** Whether the field is required */
  required?: boolean

  /**
   * Number of months/calendars to show in the calendar popup
   * Defaults to 2 for range mode, 1 for single mode
   */
  numberOfMonths?: number

  /**
   * Enable time selection alongside date selection
   * When true, displays a time picker button next to the date picker
   */
  enableTime?: boolean

  /**
   * Current time value in HH:mm format (e.g., "14:30")
   * Only used when enableTime is true
   */
  timeValue?: string

  /**
   * Callback fired when time changes
   * Only used when enableTime is true
   */
  onTimeChange?: (time: string) => void

  /**
   * Time interval in minutes for the time picker dropdown
   * Defaults to 60 (hourly intervals: 00:00, 01:00, 02:00, etc.)
   * Common values: 60 (hourly), 30 (half-hour), 15 (quarter-hour)
   */
  timeInterval?: number

  /**
   * Placeholder text for time picker when no time is selected
   */
  timePlaceholder?: string

  /**
   * Label for "from" date/time in range+time mode (e.g., "START")
   */
  rangeFromLabel?: string

  /**
   * Label for "to" date/time in range+time mode (e.g., "END")
   */
  rangeToLabel?: string

  /**
   * Layout for range+time mode controls
   * - 'stacked': FROM and TO sections on separate lines (default)
   * - 'side-by-side': FROM and TO sections in a row
   * Only applies when mode='range' and enableTime=true
   */
  rangeLayout?: 'stacked' | 'side-by-side'

  clearInvalidInput?: boolean
}

/** Day names indexed by day of week (0-6) */
const DAY_NAMES = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const;

/** Regular expressions for parsing various date input formats */
const DATE_FORMAT_PATTERNS = {
  ISO: /^(\d{4})-(\d{1,2})-(\d{1,2})$/, // YYYY-MM-DD
  UK: /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/, // DD/MM/YYYY
  US: /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/, // MM/DD/YYYY (handled differently in parsing)
  DOT: /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/, // DD.MM.YYYY
} as const;

/** Navigation keys that should be allowed even in read-only mode */
const ALLOWED_NAVIGATION_KEYS = [
  'Tab',
  'Escape',
  'Enter',
  'ArrowLeft',
  'ArrowRight',
  'Home',
  'End',
] as const;

const DEFAULT_TIME = '00:00:00';

/**
 * Parse a date string in YYYY-MM-DD format to a Date object
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns Parsed Date object
 */
function parseDate(dateString: string): Date {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
}

/**
 * Format a Date object to YYYY-MM-DD string
 * @param date - Date object to format
 * @returns Formatted date string in YYYY-MM-DD format
 */
function formatDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Check if a Date object is valid
 * @param date - Date object to validate
 * @returns True if the date is valid
 */
function isValidDate(date: Date): boolean {
  return (
    date instanceof Date && !isNaN(date.getTime()) && date.getFullYear() > 1900
  );
}

/**
 * Parse various date input formats to a Date object
 * Supports: YYYY-MM-DD, DD/MM/YYYY, MM/DD/YYYY, DD.MM.YYYY
 * @param input - User input string
 * @returns Parsed Date object or null if parsing fails
 */
function parseInputDate(input: string): Date | null {
  if (!input) return null;

  // Try ISO format first (YYYY-MM-DD)
  const isoMatch = DATE_FORMAT_PATTERNS.ISO.exec(input);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  }

  // Try UK format (DD/MM/YYYY)
  const ukMatch = DATE_FORMAT_PATTERNS.UK.exec(input);
  if (ukMatch) {
    const [, dayStr, monthStr, yearStr] = ukMatch;
    const day = parseInt(dayStr);
    const month = parseInt(monthStr);
    const year = parseInt(yearStr);
    const parsedDate = new Date(year, month - 1, day);
    // Validate it's actually UK format (day <= 12 to distinguish from US format)
    if (day > 12 || month <= 12) {
      // Overflow check: ensure parsed date matches input
      if (
        parsedDate.getDate() !== day ||
        parsedDate.getMonth() !== month - 1 ||
        parsedDate.getFullYear() !== year
      ) {
        return null;
      }
      return parsedDate;
    }
  }

  // Try US format (MM/DD/YYYY) - only if day > 12
  const usMatch = DATE_FORMAT_PATTERNS.US.exec(input);
  if (usMatch) {
    const [, month, day, year] = usMatch;
    if (parseInt(month) <= 12 && parseInt(day) > 12) {
      return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    }
  }

  // Try dot format (DD.MM.YYYY)
  const dotMatch = DATE_FORMAT_PATTERNS.DOT.exec(input);
  if (dotMatch) {
    const [, dayStr, monthStr, yearStr] = dotMatch;
    const day = parseInt(dayStr);
    const month = parseInt(monthStr);
    const year = parseInt(yearStr);
    const parsedDate = new Date(year, month - 1, day);
    // Overflow check: ensure parsed date matches input
    if (
      parsedDate.getDate() !== day ||
      parsedDate.getMonth() !== month - 1 ||
      parsedDate.getFullYear() !== year
    ) {
      return null;
    }
    return parsedDate;
  }

  // Return null for invalid input instead of falling back to native Date parsing
  return null;
}

/**
 * Parse a combined date+time string into separate date and time parts
 * @param dateTimeString - Combined string in format "YYYY-MM-DD HH:mm" or just "YYYY-MM-DD"
 * @returns Object with date and optional time parts
 */
function parseDateTimeString(dateTimeString: string | undefined): {
  date?: string
  time?: string
} {
  if (!dateTimeString) return {};

  const trimmed = dateTimeString.trim();

  const match = /^(\d{4}-\d{2}-\d{2})(?:\s+(\d{2}:\d{2}:\d{2}))?$/.exec(trimmed);

  if (match) {
    return {
      date: match[1],
      time: match[2],
    };
  }

  const dateMatch = /^(\d{4}-\d{2}-\d{2})/.exec(trimmed);
  if (dateMatch) {
    const datePart = dateMatch[1];
    const timeMatch = /\b(\d{2}:\d{2}:\d{2})\b/.exec(trimmed);
    return {
      date: datePart,
      time: timeMatch?.[1],
    };
  }

  return { date: /^\d{4}-\d{2}-\d{2}$/.exec(trimmed) ? trimmed : undefined };
}

/**
 * Combine date and time strings into a single string
 * @param date - Date string in YYYY-MM-DD format
 * @param time - Time string in HH:MM:SS format (optional)
 * @returns Combined string "YYYY-MM-DD HH:MM:SS" or just "YYYY-MM-DD" if no time
 */
function combineDateTimeString(
  date: string | undefined,
  time: string | undefined
): string | undefined {
  if (!date) return undefined;

  if (time) {
    return `${date} ${time}`;
  }

  return date;
}

/**
 * Format a date string in YYYY-MM-DD format to DD/MM/YYYY display format
 * @param dateStr - Date string in YYYY-MM-DD format
 * @returns Formatted display string in DD/MM/YYYY format
 */
function formatDisplayDate(dateStr: string): string {
  try {
    const date = parseDate(dateStr);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  } catch {
    return dateStr;
  }
}

const createBoundDayButton = (
  dateRange: { from?: string; to?: string } | undefined,
  setDateRange: (range: { from?: string; to?: string }) => void,
  onRangeChange?: (range: { from?: string; to?: string }) => void
) => {
  function BoundDayButton(
    props: React.ComponentProps<typeof CalendarDayButton>
  ) {
    return (
      <CalendarDayButton
        {...props}
        onDoubleClick={() => {
          if (dateRange?.from) {
            const newRange = {
              from: undefined,
              to: dateRange.to,
            };
            setDateRange(newRange);
            onRangeChange?.(newRange);
          }
        }}
      />
    );
  }
  BoundDayButton.displayName = 'BoundDayButton';
  return BoundDayButton;
};

/**
 * Custom hook to manage time state synchronization for single mode
 */
function useSingleTimeSync(value?: string, timeValue?: string) {
  const [time, setTime] = React.useState<string | undefined>(timeValue);

  React.useEffect(() => {
    if (value) {
      const parsed = parseDateTimeString(value);
      setTime(parsed.time ?? timeValue);
    } else {
      setTime(timeValue);
    }
  }, [value, timeValue]);

  return { time, setTime };
}

/**
 * Custom hook to manage time state synchronization for range mode
 */
function useRangeTimeSync(rangeValue?: { from?: string; to?: string }) {
  const [rangeTime, setRangeTime] = React.useState<{
    from?: string
    to?: string
  }>({});

  React.useEffect(() => {
    if (rangeValue) {
      const fromParsed = parseDateTimeString(rangeValue.from);
      const toParsed = parseDateTimeString(rangeValue.to);

      setRangeTime({
        from: fromParsed.time,
        to: toParsed.time,
      });
    }
  }, [rangeValue]);

  return { rangeTime, setRangeTime };
}
/**
 * Custom hook to manage state synchronization with props
 */
function useStateSynchronization({
  mode,
  value,
  rangeValue,
  required,
  requiredErrorMessage,
  onValidationChange,
  formatDateForDisplay,
  formatRangeDisplay,
}: {
  mode: 'single' | 'range'
  value?: string
  rangeValue?: { from?: string; to?: string }
  required: boolean
  requiredErrorMessage?: string
  onValidationChange?: (isValid: boolean, error: string) => void
  formatDateForDisplay: (dateStr: string) => string
  formatRangeDisplay: (from?: string, to?: string) => string
}) {
  const [date, setDate] = React.useState<string | undefined>(
    mode === 'range' ? undefined : value
  );
  const [dateRange, setDateRange] = React.useState<
  { from?: string; to?: string } | undefined
  >(mode === 'range' ? rangeValue : undefined);
  const [rawInputValue, setRawInputValue] = React.useState<string>('');
  const [rawRangeFromInput, setRawRangeFromInput] = React.useState<string>('');
  const [rawRangeToInput, setRawRangeToInput] = React.useState<string>('');

  React.useEffect(() => {
    if (mode === 'single') {
      setDate(value);
      if (value) {
        setRawInputValue(formatDateForDisplay(value));
      } else {
        setRawInputValue('');
        const requiredMsg = requiredErrorMessage ?? 'This field is required';
        onValidationChange?.(!required, required ? requiredMsg : '');
      }
    }
  }, [
    value,
    mode,
    required,
    requiredErrorMessage,
    onValidationChange,
    formatDateForDisplay,
  ]);

  React.useEffect(() => {
    if (mode === 'range') {
      setDateRange(rangeValue);
      if (rangeValue?.from || rangeValue?.to) {
        setRawInputValue(formatRangeDisplay(rangeValue.from, rangeValue.to));
      } else {
        setRawInputValue('');
        const requiredMsg = requiredErrorMessage ?? 'This field is required';
        onValidationChange?.(!required, required ? requiredMsg : '');
      }
    }
  }, [
    rangeValue,
    mode,
    required,
    requiredErrorMessage,
    onValidationChange,
    formatRangeDisplay,
  ]);

  React.useEffect(() => {
    if (mode === 'range') {
      setRawRangeFromInput('');
      setRawRangeToInput('');
    }
  }, [rangeValue, mode]);

  return {
    date,
    setDate,
    dateRange,
    setDateRange,
    rawInputValue,
    setRawInputValue,
    rawRangeFromInput,
    setRawRangeFromInput,
    rawRangeToInput,
    setRawRangeToInput,
  };
}

/**
 * Custom hook to manage validation state and logic
 */
function useValidationState({
  required,
  requiredErrorMessage,
  restrictToDayOfWeek,
  disabled,
}: {
  required: boolean
  requiredErrorMessage?: string
  restrictToDayOfWeek?: number
  disabled?: Matcher | Matcher[]
}) {
  const [isValid, setIsValid] = React.useState(true);
  const [validationError, setValidationError] = React.useState<string>('');
  const [rangeFromDateValid, setRangeFromDateValid] = React.useState(true);
  const [rangeFromDateError, setRangeFromDateError] = React.useState<string>('');
  const [rangeToDateValid, setRangeToDateValid] = React.useState(true);
  const [rangeToDateError, setRangeToDateError] = React.useState<string>('');

  const isDateDisabled = React.useCallback(
    (date: Date, disabledProp: Matcher | Matcher[]): boolean => {
      if (typeof disabledProp === 'boolean') return disabledProp;
      if (Array.isArray(disabledProp)) {
        return disabledProp.some((matcher) => {
          if (typeof matcher === 'boolean') return matcher;
          if (typeof matcher === 'function') return matcher(date);
          if (
            matcher &&
            typeof matcher === 'object' &&
            'dayOfWeek' in matcher
          ) {
            const dayOfWeek = Array.isArray(matcher.dayOfWeek)
              ? matcher.dayOfWeek
              : [matcher.dayOfWeek];
            return dayOfWeek.includes(date.getDay());
          }
          return false;
        });
      }
      if (typeof disabledProp === 'function') return disabledProp(date);
      if (
        disabledProp &&
        typeof disabledProp === 'object' &&
        'dayOfWeek' in disabledProp
      ) {
        const dayOfWeek = Array.isArray(disabledProp.dayOfWeek)
          ? disabledProp.dayOfWeek
          : [disabledProp.dayOfWeek];
        return dayOfWeek.includes(date.getDay());
      }
      return false;
    },
    []
  );

  const validateInput = React.useCallback(
    (
      input: string
    ): { isValid: boolean; error: string; parsedDate: Date | null } => {
      if (!input.trim()) {
        if (required) {
          const requiredMsg = requiredErrorMessage ?? 'Date is required';
          return { isValid: false, error: requiredMsg, parsedDate: null };
        }
        return { isValid: true, error: '', parsedDate: null };
      }

      const { date: datePart } = parseDateTimeString(input);
      const dateToValidate = datePart ?? input;
      const parsedDate = parseInputDate(dateToValidate);

      if (!parsedDate || !isValidDate(parsedDate)) {
        return {
          isValid: false,
          error: 'Please enter a valid date in DD/MM/YYYY format',
          parsedDate: null,
        };
      }

      if (
        restrictToDayOfWeek !== undefined &&
        parsedDate.getDay() !== restrictToDayOfWeek
      ) {
        const dayName = DAY_NAMES[restrictToDayOfWeek];
        return {
          isValid: false,
          error: `Please select a ${dayName}`,
          parsedDate: null,
        };
      }

      if (disabled && isDateDisabled(parsedDate, disabled)) {
        return {
          isValid: false,
          error: 'This date is not available',
          parsedDate: null,
        };
      }

      return { isValid: true, error: '', parsedDate };
    },
    [
      required,
      requiredErrorMessage,
      restrictToDayOfWeek,
      disabled,
      isDateDisabled,
    ]
  );

  return {
    isValid,
    setIsValid,
    validationError,
    setValidationError,
    rangeFromDateValid,
    setRangeFromDateValid,
    rangeFromDateError,
    setRangeFromDateError,
    rangeToDateValid,
    setRangeToDateValid,
    rangeToDateError,
    setRangeToDateError,
    validateInput,
    isDateDisabled,
  };
}

/**
 * Custom hook to manage disabled days matchers
 */
function useDisabledDaysMatchers({
  restrictToDayOfWeek,
  minDate,
  maxDate,
  selectableDates,
  mode,
  disabled,
}: {
  restrictToDayOfWeek?: number
  minDate?: string
  maxDate?: string
  selectableDates?: string[]
  mode: 'single' | 'range'
  disabled?: Matcher | Matcher[]
}) {
  const addDayOfWeekMatcher = React.useCallback(
    (matchers: Matcher[], dayOfWeek?: number) => {
      if (dayOfWeek !== undefined) {
        const disabledDaysOfWeek = [0, 1, 2, 3, 4, 5, 6].filter(
          (day) => day !== dayOfWeek
        );
        matchers.push({ dayOfWeek: disabledDaysOfWeek });
      }
    },
    []
  );

  const addDateRangeMatcher = React.useCallback(
    (matchers: Matcher[], minDateStr?: string, maxDateStr?: string) => {
      if (minDateStr) {
        try {
          const minDateObj = parseDate(minDateStr);
          if (isValidDate(minDateObj)) {
            matchers.push({ before: minDateObj });
          }
        } catch {
          // Silently ignore invalid minDate format (important-comment)
        }
      }

      if (maxDateStr) {
        try {
          const maxDateObj = parseDate(maxDateStr);
          if (isValidDate(maxDateObj)) {
            matchers.push({ after: maxDateObj });
          }
        } catch {
          // Silently ignore invalid maxDate format (important-comment)
        }
      }
    },
    []
  );

  const addSelectableDatesMatcher = React.useCallback(
    (matchers: Matcher[], selectableDatesArr?: string[], modeStr?: string) => {
      if (selectableDatesArr && selectableDatesArr.length > 0 && modeStr === 'single') {
        const selectableDateObjects = selectableDatesArr
          .map((dateStr) => {
            try {
              return parseDate(dateStr);
            } catch {
              return null;
            }
          })
          .filter((date): date is Date => date !== null && isValidDate(date));

        if (selectableDateObjects.length > 0) {
          matchers.push((date: Date) => {
            return !selectableDateObjects.some(
              (selectableDate) =>
                selectableDate.getFullYear() === date.getFullYear() &&
                selectableDate.getMonth() === date.getMonth() &&
                selectableDate.getDate() === date.getDate()
            );
          });
        }
      }
    },
    []
  );

  const areAllDaysDisabled = React.useCallback((days: unknown[]): boolean => {
    return days.length === 0 || days.length === 7;
  }, []);

  const getDaysFromMatcher = React.useCallback((matcher: unknown): number[] => {
    if (
      matcher &&
      typeof matcher === 'object' &&
      matcher !== null &&
      'dayOfWeek' in matcher
    ) {
      const m = matcher as { dayOfWeek: number | number[] };
      return Array.isArray(m.dayOfWeek) ? m.dayOfWeek : [m.dayOfWeek];
    }
    return [];
  }, []);

  const disabledDays = React.useMemo((): Matcher | Matcher[] | undefined => {
    const matchers: Matcher[] = [];

    addDayOfWeekMatcher(matchers, restrictToDayOfWeek);
    addDateRangeMatcher(matchers, minDate, maxDate);
    addSelectableDatesMatcher(matchers, selectableDates, mode);

    if (disabled && typeof disabled !== 'boolean') {
      if (Array.isArray(disabled)) {
        matchers.push(...disabled);
      } else {
        matchers.push(disabled);
      }
    }

    if (matchers.length === 0) return undefined;
    if (matchers.length === 1) return matchers[0];
    return matchers.flat();
  }, [
    restrictToDayOfWeek,
    disabled,
    minDate,
    maxDate,
    selectableDates,
    mode,
    addDayOfWeekMatcher,
    addDateRangeMatcher,
    addSelectableDatesMatcher,
  ]);

  const isDisabled = React.useMemo(() => {
    if (disabled === true) return true;
    if (!disabled) return false;
    if (typeof disabled === 'function') return false;

    if (Array.isArray(disabled)) {
      return areAllDaysDisabled(disabled);
    }

    const days = getDaysFromMatcher(disabled);
    return areAllDaysDisabled(days);
  }, [disabled, areAllDaysDisabled, getDaysFromMatcher]);

  return { disabledDays, isDisabled };
}

/**
 * CustomDatePicker Component
 *
 * A flexible date picker supporting:
 * - Single date or date range selection
 * - Optional time selection (single or range)
 * - Manual input or calendar-only modes
 * - Day-of-week, min/max, and selectable date restrictions
 *
 * ---
 *
 * Props Reference
 *
 * Basic Props:
 * - mode: 'single' | 'range' — Selection mode (default: 'single')
 * - value: string — Current date value (single mode, format: 'YYYY-MM-DD' or 'YYYY-MM-DD HH:mm:ss' if time enabled)
 * - onChange: (date: string) => void — Callback for date changes (single mode)
 * - placeholder: string — Placeholder text (single mode)
 * - disabled: boolean | Matcher | Matcher[] — Disable all dates, or use Matcher(s) for custom logic (see below)
 * - minDate: string — Minimum selectable date (inclusive, 'YYYY-MM-DD')
 * - maxDate: string — Maximum selectable date (inclusive, 'YYYY-MM-DD')
 * - selectableDates: string[] — Array of valid dates (single mode only, disables all others)
 *
 * Range Mode Props:
 * - rangeValue: {from?: string; to?: string} — Range value
 *   (format: 'YYYY-MM-DD' or 'YYYY-MM-DD HH:mm:ss' if time enabled)
 * - onRangeChange: (range: {from?: string; to?: string}) => void — Callback for range changes
 * - rangePlaceholder: string — Placeholder for range mode
 * - numberOfMonths: number — Number of calendars to display (default: 2 for range, 1 for single)
 *
 * Time Selection Props:
 * - enableTime: boolean — Enable time picker (single or range)
 * - timeInterval: number — Time interval in minutes (default: 60, e.g. 15 for quarter-hour)
 * - timePlaceholder: string — Placeholder for time input
 * - timeValue: string — Current time value (single mode, e.g. '14:30:00')
 * - onTimeChange: (time: string) => void — Callback for time changes (single mode)
 * - rangeFromLabel: string — Label for 'from' in range+time mode
 * - rangeToLabel: string — Label for 'to' in range+time mode
 * - rangeLayout: 'stacked' | 'side-by-side' — Layout for range+time controls
 *
 * Advanced Props:
 * - restrictToDayOfWeek: 0-6 — Restrict to specific day (0=Sunday, 1=Monday, ...)
 * - allowManualInput: boolean — Allow manual date entry (default: true unless restricted)
 * - required: boolean — Mark field as required
 * - showValidationError: boolean — Show validation errors inline (default: true)
 * - onValidationChange: (isValid: boolean, errorMessage?: string) => void — Validation callback
 * - errorMessage: string — Custom error message
 * - className: string — Additional CSS classes for the container
 * - buttonClassName: string — Additional CSS classes for the input/button
 * - clearInvalidInput: boolean — When true, clears invalid inputs without showing errors;
 *   when false (default), shows validation errors
 *
 * Matcher type for 'disabled' prop:
 * - boolean: disables all dates
 * - function: (date: Date) => boolean — return true to disable a date
 * - object: { dayOfWeek?: number | number[]; before?: Date; after?: Date } — disables by day, before, or after
 *
 * Notes:
 * - If both minDate/maxDate and selectableDates are set, only dates in selectableDates
 *   within the min/max range are enabled.
 * - In range mode, both from and to values use the same format as single mode.
 * - Time selection appends 'HH:mm:ss' to the date string.
 * - If restrictToDayOfWeek is set, manual input is disabled by default.
 *
 * @example
 * // See below for usage examples
 */
export function CustomDatePicker(props: Readonly<CustomDatePickerProps>) {
  const {
    value,
    onChange,
    mode = 'single',
    rangeValue,
    onRangeChange,
    disabled,
    minDate,
    maxDate,
    selectableDates,
    placeholder = 'Pick a date',
    rangePlaceholder = 'Pick a date range',
    className,
    buttonClassName,
    restrictToDayOfWeek,
    allowManualInput,
    onValidationChange,
    showValidationError = true,
    errorMessage,
    requiredErrorMessage: _requiredErrorMessage,
    required = false,
    numberOfMonths,
    enableTime = false,
    timeValue,
    onTimeChange,
    timeInterval = 60,
    timePlaceholder = '12:00:00',
    rangeFromLabel,
    rangeToLabel,
    rangeLayout = 'stacked',
    clearInvalidInput = false,
  } = props;

  // Ensure requiredErrorMessage is always defined in render scope
  const requiredErrorMessage =
    _requiredErrorMessage ?? props.requiredErrorMessage;

  const shouldUseSingleTime = enableTime && mode === 'single';
  const shouldUseRangeTime = enableTime && mode === 'range';

  const singleTimeSync = useSingleTimeSync(
    shouldUseSingleTime ? value : undefined,
    shouldUseSingleTime ? timeValue : undefined
  );
  const rangeTimeSync = useRangeTimeSync(
    shouldUseRangeTime ? rangeValue : undefined
  );

  const time = shouldUseSingleTime ? singleTimeSync.time : undefined;
  const setTime = singleTimeSync.setTime;
  const rangeTime = React.useMemo(
    () => (shouldUseRangeTime ? rangeTimeSync.rangeTime : {}),
    [shouldUseRangeTime, rangeTimeSync.rangeTime]
  );
  const setRangeTime = rangeTimeSync.setRangeTime;

  const stateSyncCallbacks = React.useMemo(
    () => ({
      formatDateForDisplay: (dateStr: string): string => {
        try {
          const dateOnly = dateStr.split(' ')[0];
          const parsedDate = parseDate(dateOnly);
          if (!isValidDate(parsedDate)) return dateOnly;

          const day = String(parsedDate.getDate()).padStart(2, '0');
          const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
          const year = parsedDate.getFullYear();
          return `${day}/${month}/${year}`;
        } catch {
          return dateStr.split(' ')[0];
        }
      },
      formatRangeDisplay: (from?: string, to?: string): string => {
        if (!from && !to) return '';

        const formatSingleDate = (dateStr: string): string => {
          try {
            const { date: datePart } = parseDateTimeString(dateStr);
            const dateOnly = datePart ?? dateStr.split(' ')[0];
            const parsedDate = parseDate(dateOnly);
            if (!isValidDate(parsedDate)) return dateOnly;

            const day = String(parsedDate.getDate()).padStart(2, '0');
            const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
            const year = parsedDate.getFullYear();
            return `${day}-${month}-${year}`;
          } catch {
            return dateStr.split(' ')[0];
          }
        };

        const fromDisplay = from ? formatSingleDate(from) : '';
        const toDisplay = to ? formatSingleDate(to) : '';
        if (fromDisplay && toDisplay) return `${fromDisplay} - ${toDisplay}`;
        if (fromDisplay) return fromDisplay;
        return '';
      },
    }),
    []
  );

  const {
    date,
    setDate,
    dateRange,
    setDateRange,
    rawInputValue,
    setRawInputValue,
    rawRangeFromInput,
    setRawRangeFromInput,
    rawRangeToInput,
    setRawRangeToInput,
  } = useStateSynchronization({
    mode,
    value,
    rangeValue,
    required,
    requiredErrorMessage,
    onValidationChange,
    formatDateForDisplay: stateSyncCallbacks.formatDateForDisplay,
    formatRangeDisplay: stateSyncCallbacks.formatRangeDisplay,
  });

  const {
    isValid,
    setIsValid,
    validationError,
    setValidationError,
    rangeFromDateValid,
    setRangeFromDateValid,
    rangeFromDateError,
    setRangeFromDateError,
    rangeToDateValid,
    setRangeToDateValid,
    rangeToDateError,
    setRangeToDateError,
    validateInput,
    isDateDisabled,
  } = useValidationState({
    required,
    requiredErrorMessage,
    restrictToDayOfWeek,
    disabled,
  });

  const { disabledDays, isDisabled } = useDisabledDaysMatchers({
    restrictToDayOfWeek,
    minDate,
    maxDate,
    selectableDates,
    mode,
    disabled,
  });

  const [isOpen, setIsOpen] = React.useState(false);
  const [isTimeOpen, setIsTimeOpen] = React.useState(false);
  const [isRangeFromTimeOpen, setIsRangeFromTimeOpen] = React.useState(false);
  const [isRangeToTimeOpen, setIsRangeToTimeOpen] = React.useState(false);
  const [isRangeFromDateOpen, setIsRangeFromDateOpen] = React.useState(false);
  const [isRangeToDateOpen, setIsRangeToDateOpen] = React.useState(false);

  const id = React.useId();

  const timeOptions = React.useMemo(() => {
    if (!enableTime) return [];

    const interval = timeInterval || 60;
    const options: string[] = [];

    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += interval) {
        const timeStr = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}:00`;
        options.push(timeStr);
      }
    }

    return options;
  }, [enableTime, timeInterval]);

  // Determine if manual input is allowed
  const isManualInputAllowed = React.useMemo(() => {
    if (isDisabled) return false;
    if (allowManualInput !== undefined) return allowManualInput;
    if (
      minDate ||
      maxDate ||
      selectableDates ||
      restrictToDayOfWeek !== undefined
    )
      return false;
    if (disabled && typeof disabled !== 'boolean') return false;
    return true;
  }, [
    isDisabled,
    allowManualInput,
    minDate,
    maxDate,
    selectableDates,
    restrictToDayOfWeek,
    disabled,
  ]);

  const handleEmptyInputBlur = React.useCallback(() => {
    if (mode === 'range') {
      setDateRange({ from: undefined, to: undefined });
      setRawInputValue('');
      onRangeChange?.({ from: undefined, to: undefined });
    } else {
      setDate('');
      onChange?.('');
    }
    setIsValid(!required);
    setValidationError(required ? 'This field is required' : '');
    onValidationChange?.(!required, required ? 'This field is required' : '');
  }, [
    mode,
    onChange,
    onRangeChange,
    onValidationChange,
    required,
    setDate,
    setDateRange,
    setIsValid,
    setRawInputValue,
    setValidationError,
  ]);

  const handleValidRangeInput = React.useCallback(
    (fromStr: string, toStr: string) => {
      const fromValidation = validateInput(fromStr);
      const toValidation = validateInput(toStr);

      let rangeOrderError = '';
      if (
        fromValidation.isValid &&
        toValidation.isValid &&
        'parsedDate' in fromValidation &&
        'parsedDate' in toValidation &&
        fromValidation.parsedDate &&
        toValidation.parsedDate &&
        fromValidation.parsedDate > toValidation.parsedDate
      ) {
        rangeOrderError = 'Start date must be before end date';
      }

      const rangeIsValid =
        fromValidation.isValid && toValidation.isValid && !rangeOrderError;
      const error =
        fromValidation.error ?? toValidation.error ?? rangeOrderError;

      setIsValid(rangeIsValid);
      setValidationError(error);
      onValidationChange?.(rangeIsValid, error);

      if (rangeIsValid && fromValidation.parsedDate && toValidation.parsedDate) {
        const fromFormatted = formatDate(fromValidation.parsedDate);
        const toFormatted = formatDate(toValidation.parsedDate);

        let fromFinal: string | undefined;
        let toFinal: string | undefined;

        if (enableTime) {
          fromFinal = combineDateTimeString(fromFormatted, rangeTime.from);
          toFinal = combineDateTimeString(toFormatted, rangeTime.to);
        } else {
          fromFinal = fromFormatted;
          toFinal = toFormatted;
        }

        const newRange = {
          from: fromFinal,
          to: toFinal,
        };
        setDateRange(newRange);
        onRangeChange?.(newRange);

        const day1 = String(fromValidation.parsedDate.getDate()).padStart(
          2,
          '0'
        );
        const month1 = String(
          fromValidation.parsedDate.getMonth() + 1
        ).padStart(2, '0');
        const year1 = fromValidation.parsedDate.getFullYear();
        const day2 = String(toValidation.parsedDate.getDate()).padStart(2, '0');
        const month2 = String(toValidation.parsedDate.getMonth() + 1).padStart(
          2,
          '0'
        );
        const year2 = toValidation.parsedDate.getFullYear();
        setRawInputValue(
          `${day1}/${month1}/${year1} - ${day2}/${month2}/${year2}`
        );
      }
    },
    [
      validateInput,
      onValidationChange,
      enableTime,
      rangeTime,
      onRangeChange,
      setDateRange,
      setRawInputValue,
      setIsValid,
      setValidationError,
    ]
  );

  const handleSingleValueInRangeMode = React.useCallback(
    (inputValue: string) => {
      const validation = validateInput(inputValue);
      setIsValid(validation.isValid);
      setValidationError(validation.error);
      onValidationChange?.(validation.isValid, validation.error);

      if (validation.isValid && validation.parsedDate) {
        const formatted = formatDate(validation.parsedDate);

        let fromFinal: string | undefined;
        if (enableTime && rangeTime.from) {
          fromFinal = combineDateTimeString(formatted, rangeTime.from);
        } else {
          fromFinal = formatted;
        }

        const newRange = { from: fromFinal, to: dateRange?.to };
        setDateRange(newRange);
        onRangeChange?.(newRange);

        const day = String(validation.parsedDate.getDate()).padStart(2, '0');
        const month = String(validation.parsedDate.getMonth() + 1).padStart(
          2,
          '0'
        );
        const year = validation.parsedDate.getFullYear();
        setRawInputValue(`${day}/${month}/${year}`);
      }
    },
    [
      validateInput,
      onValidationChange,
      enableTime,
      rangeTime,
      dateRange,
      onRangeChange,
      setDateRange,
      setRawInputValue,
      setIsValid,
      setValidationError,
    ]
  );

  const handleSingleDateInputBlur = React.useCallback(
    (inputValue: string) => {
      const validation = validateInput(inputValue);

      if (validation.isValid && validation.parsedDate) {
        const formatted = formatDate(validation.parsedDate);

        let finalValue = formatted;
        if (enableTime && time) {
          finalValue = combineDateTimeString(formatted, time) ?? formatted;
        }

        setDate(finalValue);
        onChange?.(finalValue);
        setIsValid(true);
        setValidationError('');
        onValidationChange?.(true, '');
      } else if (clearInvalidInput) {
        setDate('');
        setRawInputValue('');
        onChange?.('');
        setIsValid(true);
        setValidationError('');
        onValidationChange?.(true, '');
      } else {
        setIsValid(validation.isValid);
        setValidationError(validation.error);
        onValidationChange?.(validation.isValid, validation.error);
      }
    },
    [
      validateInput,
      onChange,
      onValidationChange,
      enableTime,
      time,
      clearInvalidInput,
      setDate,
      setRawInputValue,
      setIsValid,
      setValidationError,
    ]
  );

  const handleInputBlur = React.useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      if (disabled || !isManualInputAllowed) return;

      const inputValue = event.target.value.trim();

      if (!inputValue) {
        handleEmptyInputBlur();
        return;
      }

      if (mode === 'range') {
        const rangeMatch = /^([^-]{1,50})\s*-\s*([^-]{1,50})$/.exec(inputValue);
        if (rangeMatch) {
          const [, fromStr, toStr] = rangeMatch;
          handleValidRangeInput(fromStr.trim(), toStr.trim());
        } else {
          handleSingleValueInRangeMode(inputValue);
        }
      } else {
        handleSingleDateInputBlur(inputValue);
      }
    },
    [
      disabled,
      isManualInputAllowed,
      mode,
      handleEmptyInputBlur,
      handleValidRangeInput,
      handleSingleValueInRangeMode,
      handleSingleDateInputBlur,
    ]
  );

  /**
   * Sync internal state with external value prop and validate
   */
  React.useEffect(() => {
    setDate(value);

    // Validate the new value
    if (value) {
      const validation = validateInput(value);
      setIsValid(validation.isValid);
      setValidationError(validation.error);
    } else {
      // This ensures Clear button properly resets validation errors
      setIsValid(true);
      setValidationError('');
    }
  }, [value, validateInput, setDate, setIsValid, setValidationError]);

  /**
   * Format display value for the input/button
   * Shows date in DD/MM/YYYY format (British format)
   */
  const formatDateForInput = React.useCallback(
    (dateStr: string): string => {
      try {
        const { date: datePart, time: timePart } = parseDateTimeString(dateStr);
        if (!datePart) return isManualInputAllowed ? dateStr : '';

        const parsedDate = parseDate(datePart);
        if (!isValidDate(parsedDate)) return isManualInputAllowed ? dateStr : '';

        const day = String(parsedDate.getDate()).padStart(2, '0');
        const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
        const year = parsedDate.getFullYear();
        const timeDisplay = timePart ? ` ${timePart}` : '';
        return `${day}/${month}/${year}${timeDisplay}`;
      } catch {
        return isManualInputAllowed ? dateStr : '';
      }
    },
    [isManualInputAllowed]
  );

  const displayValue = React.useMemo(() => {
    if (mode === 'range') {
      if (!dateRange?.from && !dateRange?.to) return '';

      const fromDisplay = dateRange.from
        ? formatDateForInput(dateRange.from)
        : '';
      const toDisplay = dateRange.to ? formatDateForInput(dateRange.to) : '';

      if (fromDisplay && toDisplay) {
        return `${fromDisplay} - ${toDisplay}`;
      } else if (fromDisplay) {
        return fromDisplay;
      }
      return '';
    }

    if (!date) return '';

    const formattedDate = formatDateForInput(date);
    if (!formattedDate) return '';

    const singleTime = time ? ` ${time}` : '';
    return `${formattedDate}${singleTime}`;
  }, [mode, dateRange, date, time, formatDateForInput]);

  /**
   * Compute the error message to display
   * Note: errorMessage prop takes precedence and is shown even when internal isValid is true
   * This allows external validation (e.g., business logic) to show errors
   */
  const displayError = React.useMemo(() => {
    if (!showValidationError || isDisabled) {
      return null;
    }

    // External errorMessage prop takes precedence - show it even if internal validation passes
    if (errorMessage) {
      return errorMessage;
    }

    // For internal validation errors, only show if isValid is false
    if (isValid || !validationError) {
      return null;
    }

    if (
      (validationError === 'This field is required' ||
        validationError === 'Date is required') &&
      requiredErrorMessage
    ) {
      return requiredErrorMessage;
    }

    return validationError;
  }, [
    showValidationError,
    isValid,
    validationError,
    isDisabled,
    errorMessage,
    requiredErrorMessage,
  ]);

  /**
   * Get ARIA label for the input/button element
   */
  const getAriaLabel = React.useCallback(() => {
    const baseLabel = mode === 'range' ? 'Date range picker' : 'Date picker';
    if (restrictToDayOfWeek !== undefined) {
      return `${baseLabel} - ${DAY_NAMES[restrictToDayOfWeek]}s only`;
    }
    return baseLabel;
  }, [mode, restrictToDayOfWeek]);

  /**
   * Handle date selection from calendar
   */
  const handleSelect = React.useCallback(
    (selected: Date | undefined) => {
      if (disabled === true) return;
      if (!selected) return;

      if (
        disabled &&
        typeof disabled !== 'boolean' &&
        isDateDisabled(selected, disabled)
      )
        return;

      const formatted = formatDate(selected);
      const validation = validateInput(formatted);

      if (validation.isValid) {
        let finalValue = formatted;
        if (enableTime) {
          const timeToUse = time ?? DEFAULT_TIME;
          finalValue = combineDateTimeString(formatted, timeToUse) ?? formatted;
        }

        setDate(finalValue);
        setIsValid(true);
        setValidationError('');
        onValidationChange?.(true, '');
        onChange?.(finalValue);
      }
    },
    [
      validateInput,
      onValidationChange,
      onChange,
      disabled,
      isDateDisabled,
      enableTime,
      time,
      setDate,
      setIsValid,
      setValidationError,
    ]
  );

  /**
   * Handle date range selection from calendar
   */
  const handleRangeSelect = React.useCallback(
    (selected: DateRange | undefined) => {
      if (disabled === true) return;
      if (!selected) return;

      if (
        selected.from &&
        selected.to &&
        selected.from.getTime() === selected.to.getTime() &&
        !dateRange?.to
      ) {
        selected = { from: selected.from, to: undefined };
      }

      const fromDateFormatted = selected.from
        ? formatDate(selected.from)
        : undefined;
      const toDateFormatted = selected.to ? formatDate(selected.to) : undefined;

      let fromFormatted: string | undefined;
      let toFormatted: string | undefined;

      if (enableTime) {
        const fromTimeToUse = rangeTime.from ?? DEFAULT_TIME;
        const toTimeToUse = rangeTime.to ?? DEFAULT_TIME;
        fromFormatted = combineDateTimeString(fromDateFormatted, fromTimeToUse);
        toFormatted = combineDateTimeString(toDateFormatted, toTimeToUse);
      } else {
        fromFormatted = fromDateFormatted;
        toFormatted = toDateFormatted;
      }

      const newRange = { from: fromFormatted, to: toFormatted };

      const fromDatePart = fromFormatted
        ? parseDateTimeString(fromFormatted).date
        : undefined;
      const toDatePart = toFormatted
        ? parseDateTimeString(toFormatted).date
        : undefined;

      const fromValidation = fromDatePart
        ? validateInput(fromDatePart)
        : { isValid: true, error: '' };
      const toValidation = toDatePart
        ? validateInput(toDatePart)
        : { isValid: true, error: '' };

      let rangeOrderError = '';
      if (selected.from && selected.to && selected.from > selected.to) {
        rangeOrderError = 'Start date must be before end date';
      }

      const rangeSelectIsValid =
        fromValidation.isValid && toValidation.isValid && !rangeOrderError;
      const error =
        fromValidation.error ?? toValidation.error ?? rangeOrderError;

      setDateRange(newRange);
      setIsValid(rangeSelectIsValid);
      setValidationError(error);
      onValidationChange?.(rangeSelectIsValid, error);
      onRangeChange?.(newRange);
    },
    [
      validateInput,
      onValidationChange,
      onRangeChange,
      disabled,
      enableTime,
      rangeTime,
      dateRange?.to,
      setDateRange,
      setIsValid,
      setValidationError,
    ]
  );

  /**
   * Handle selection of "from" date in range+time mode
   */
  const handleSelectFromDate = React.useCallback(
    (selected: Date | undefined) => {
      if (!selected) return;

      const dateStr = formatDate(selected);

      const timeToUse = rangeTime.from ?? DEFAULT_TIME;
      const newFrom = combineDateTimeString(dateStr, timeToUse);

      const newRange = { from: newFrom, to: dateRange?.to };
      setDateRange(newRange);
      onRangeChange?.(newRange);
      setIsRangeFromDateOpen(false);
    },
    [dateRange, rangeTime, onRangeChange, setDateRange]
  );

  /**
   * Handle selection of "to" date in range+time mode
   */
  const handleSelectToDate = React.useCallback(
    (selected: Date | undefined) => {
      if (!selected) return;

      const dateStr = formatDate(selected);

      const timeToUse = rangeTime.to ?? DEFAULT_TIME;
      const newTo = combineDateTimeString(dateStr, timeToUse);

      const newRange = { from: dateRange?.from, to: newTo };
      setDateRange(newRange);
      onRangeChange?.(newRange);
      setIsRangeToDateOpen(false);
    },
    [dateRange, rangeTime, onRangeChange, setDateRange]
  );

  /**
   * Handle manual input changes - only update raw input value immediately
   */
  const handleInputChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (disabled || !isManualInputAllowed) return;

      const inputValue = event.target.value;
      setRawInputValue(inputValue);

      if (inputValue.trim() === '') {
        // Fire required validation immediately
        if (required) {
          setIsValid(false);
          setValidationError('This field is required');
          onValidationChange?.(false, 'This field is required');
        } else {
          setIsValid(true);
          setValidationError('');
          onValidationChange?.(true, '');
        }
      } else {
        // Validate non-empty input
        const validation = validateInput(inputValue);
        setIsValid(validation.isValid);
        setValidationError(validation.error);
        onValidationChange?.(validation.isValid, validation.error);
      }
    },
    [
      disabled,
      isManualInputAllowed,
      required,
      onValidationChange,
      validateInput,
      setIsValid,
      setRawInputValue,
      setValidationError,
    ]
  );

  /**
   * Handle keyboard events on the input
   */
  const handleInputKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (disabled) {
        event.preventDefault();
        return;
      }
      // In read-only mode, prevent typing but allow navigation
      if (
        !isManualInputAllowed &&
        !ALLOWED_NAVIGATION_KEYS.includes(
          event.key as (typeof ALLOWED_NAVIGATION_KEYS)[number]
        )
      ) {
        event.preventDefault();
        return;
      }

      if (event.key === ' ') {
        event.preventDefault();
        setIsOpen((prev) => !prev);
        return;
      }

      if (event.key === 'Enter') {
        event.preventDefault();
        setIsOpen((prev) => !prev);
        return;
      }

      // Close popover on Escape
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    },
    [isManualInputAllowed, disabled]
  );

  /**
   * Toggle calendar popover
   */
  const handleToggleCalendar = React.useCallback(() => {
    if (disabled) return;
    setIsOpen((prev) => !prev);
  }, [disabled]);

  const handleTimeChange = React.useCallback(
    (selectedTime: string) => {
      setTime(selectedTime);
      setIsTimeOpen(false);

      if (date) {
        const datePart = parseDateTimeString(date).date ?? date;
        const combined = combineDateTimeString(datePart, selectedTime);
        if (combined) {
          setDate(combined);
          onChange?.(combined);
        }
      }

      onTimeChange?.(selectedTime);
    },
    [date, onChange, onTimeChange, setTime, setDate]
  );

  const handleRangeFromTimeChange = React.useCallback(
    (selectedTime: string) => {
      const newRangeTime = { ...rangeTime, from: selectedTime };
      setRangeTime(newRangeTime);
      setIsRangeFromTimeOpen(false);

      if (dateRange?.from) {
        const fromParsed = parseDateTimeString(dateRange.from);
        const newFrom = combineDateTimeString(fromParsed.date, selectedTime);
        const newRange = { from: newFrom, to: dateRange.to };
        setDateRange(newRange);
        onRangeChange?.(newRange);
      }
    },
    [rangeTime, dateRange, onRangeChange, setRangeTime, setDateRange]
  );

  const handleRangeToTimeChange = React.useCallback(
    (selectedTime: string) => {
      const newRangeTime = { ...rangeTime, to: selectedTime };
      setRangeTime(newRangeTime);
      setIsRangeToTimeOpen(false);

      if (dateRange?.to) {
        const toParsed = parseDateTimeString(dateRange.to);
        const newTo = combineDateTimeString(toParsed.date, selectedTime);
        const newRange = { from: dateRange.from, to: newTo };
        setDateRange(newRange);
        onRangeChange?.(newRange);
      }
    },
    [rangeTime, dateRange, onRangeChange, setRangeTime, setDateRange]
  );

  /**
   * Handle manual input for "from" date in range+time mode
   */
  const handleRangeFromInputChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (disabled) return;
      setRawRangeFromInput(event.target.value);
    },
    [disabled, setRawRangeFromInput]
  );

  /**
   * Handle manual input for "to" date in range+time mode
   */
  const handleRangeToInputChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (disabled) return;
      setRawRangeToInput(event.target.value);
    },
    [disabled, setRawRangeToInput]
  );

  /**
   * Handle blur for "from" date input in range+time mode
   */
  const handleRangeFromInputBlur = React.useCallback(() => {
    if (!rawRangeFromInput.trim()) {
      setRawRangeFromInput('');
      setRangeFromDateValid(true);
      setRangeFromDateError('');
      return;
    }

    const parsedDate = parseInputDate(rawRangeFromInput);
    if (parsedDate && isValidDate(parsedDate)) {
      const dateStr = formatDate(parsedDate);
      const validation = validateInput(dateStr);

      if (validation.isValid) {
        const timeToUse = rangeTime.from ?? DEFAULT_TIME;
        const newFrom = combineDateTimeString(dateStr, timeToUse);
        const newRange = { from: newFrom, to: dateRange?.to };
        setDateRange(newRange);
        onRangeChange?.(newRange);
        setRawRangeFromInput('');
        setRangeFromDateValid(true);
        setRangeFromDateError('');
      } else if (clearInvalidInput) {
        setRawRangeFromInput('');
        setRangeFromDateValid(true);
        setRangeFromDateError('');
      } else {
        setRangeFromDateValid(false);
        setRangeFromDateError(validation.error);
      }
    } else if (clearInvalidInput) {
      setRawRangeFromInput('');
      setRangeFromDateValid(true);
      setRangeFromDateError('');
    } else {
      setRangeFromDateValid(false);
      setRangeFromDateError('Please enter a valid date in DD/MM/YYYY format');
    }
  }, [
    rawRangeFromInput,
    validateInput,
    rangeTime,
    dateRange,
    onRangeChange,
    clearInvalidInput,
    setDateRange,
    setRangeFromDateError,
    setRangeFromDateValid,
    setRawRangeFromInput,
  ]);

  /**
   * Handle blur for "to" date input in range+time mode
   */
  const handleRangeToInputBlur = React.useCallback(() => {
    if (!rawRangeToInput.trim()) {
      setRawRangeToInput('');
      setRangeToDateValid(true);
      setRangeToDateError('');
      return;
    }

    const parsedDate = parseInputDate(rawRangeToInput);
    if (parsedDate && isValidDate(parsedDate)) {
      const dateStr = formatDate(parsedDate);
      const validation = validateInput(dateStr);

      if (validation.isValid) {
        const timeToUse = rangeTime.to ?? DEFAULT_TIME;
        const newTo = combineDateTimeString(dateStr, timeToUse);
        const newRange = { from: dateRange?.from, to: newTo };
        setDateRange(newRange);
        onRangeChange?.(newRange);
        setRawRangeToInput('');
        setRangeToDateValid(true);
        setRangeToDateError('');
      } else if (clearInvalidInput) {
        setRawRangeToInput('');
        setRangeToDateValid(true);
        setRangeToDateError('');
      } else {
        setRangeToDateValid(false);
        setRangeToDateError(validation.error);
      }
    } else if (clearInvalidInput) {
      setRawRangeToInput('');
      setRangeToDateValid(true);
      setRangeToDateError('');
    } else {
      setRangeToDateValid(false);
      setRangeToDateError('Please enter a valid date in DD/MM/YYYY format');
    }
  }, [
    rawRangeToInput,
    validateInput,
    rangeTime,
    dateRange,
    onRangeChange,
    clearInvalidInput,
    setDateRange,
    setRangeToDateError,
    setRangeToDateValid,
    setRawRangeToInput,
  ]);

  /**
   * Safely parse date string for Calendar component
   * Returns undefined for invalid dates to prevent Calendar crashes
   */
  const safeParseDate = React.useCallback(
    (dateString: string): Date | undefined => {
      if (
        !dateString ||
        typeof dateString !== 'string' ||
        dateString.trim() === ''
      ) {
        return undefined;
      }

      const { date: datePart } = parseDateTimeString(dateString);
      if (!datePart) return undefined;

      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (!dateRegex.test(datePart)) {
        return undefined;
      }

      try {
        const parsed = parseDate(datePart);
        return isValidDate(parsed) ? parsed : undefined;
      } catch {
        return undefined;
      }
    },
    []
  );

  /**
   * Common CSS classes for input/button with error state
   * Note: Shows error styling when either internal validation fails OR external errorMessage is provided
   */
  const baseClasses = React.useMemo(() => {
    // Show error styling if internal validation fails OR if external errorMessage is provided
    const hasError = ((!isValid && showValidationError) || (showValidationError && errorMessage)) && !disabled;
    const errorClasses = hasError
      ? 'border-destructive focus:border-destructive ring-destructive/20 dark:ring-destructive/40'
      : '';

    return `
  flex h-9 w-full items-center rounded-md border 
      ${hasError ? 'border-destructive' : 'border-input'}
      bg-background px-3 py-2 text-sm ring-offset-background 
      placeholder:text-muted-foreground focus:outline-none focus:ring-2 
      focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed 
      disabled:opacity-50 font-normal pl-10 cursor-pointer
      text-foreground transition-[color,box-shadow]
      ${errorClasses}
      ${buttonClassName ?? ''}
    `
      .trim()
      .replace(/\s+/g, ' ');
  }, [buttonClassName, isValid, showValidationError, disabled, errorMessage]);

  const getRangeInputClasses = React.useCallback(
    (isInputValid: boolean) => {
      const errorClasses =
        !isInputValid && showValidationError && !disabled
          ? 'border-destructive focus:border-destructive ring-destructive/20 dark:ring-destructive/40'
          : '';

      return `
  flex h-9 w-full items-center rounded-md border 
      ${!isInputValid && showValidationError && !disabled ? 'border-destructive' : 'border-input'}
      bg-background px-3 py-2 text-sm ring-offset-background 
      placeholder:text-muted-foreground focus:outline-none focus:ring-2 
      focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed 
      disabled:opacity-50 font-normal pl-10 cursor-pointer
      text-foreground transition-[color,box-shadow]
      ${errorClasses}
      ${buttonClassName ?? ''}
    `
        .trim()
        .replace(/\s+/g, ' ');
    },
    [buttonClassName, showValidationError, disabled]
  );

  const BoundDayButton = React.useMemo(
    () => createBoundDayButton(dateRange, setDateRange, onRangeChange),
    [dateRange, onRangeChange, setDateRange]
  );

  return (
    <div className={'w-full' + (className ? ' ' + className : '')}>
      {!(enableTime && mode === 'range') && (
        <div className="flex gap-2 items-center w-full">
          <Popover open={isOpen} onOpenChange={setIsOpen}>
            <PopoverTrigger asChild>
              <div className="relative w-full">
                {isManualInputAllowed && !isDisabled ? (
                  <input
                    type="text"
                    className={baseClasses}
                    aria-label={getAriaLabel()}
                    aria-haspopup="dialog"
                    aria-invalid={!isValid}
                    aria-describedby={
                      !isValid && showValidationError
                        ? `${id}-error`
                        : undefined
                    }
                    aria-required={required && !isDisabled}
                    disabled={isDisabled}
                    tabIndex={isDisabled ? -1 : 0}
                    value={rawInputValue}
                    placeholder={
                      mode === 'range' ? rangePlaceholder : placeholder
                    }
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    onKeyDown={handleInputKeyDown}
                    onClick={() => setIsOpen(true)}
                    readOnly={false}
                    autoComplete="off"
                  />
                ) : (
                  <button
                    type="button"
                    onClick={handleToggleCalendar}
                    className={baseClasses}
                    aria-label={getAriaLabel()}
                    aria-expanded={isOpen}
                    aria-haspopup="dialog"
                    disabled={isDisabled}
                    tabIndex={isDisabled ? -1 : 0}
                  >
                    {displayValue || placeholder}
                  </button>
                )}
                <CalendarDays
                  className={[
                    'absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4',
                    'text-muted-foreground pointer-events-none',
                  ].join(' ')}
                  aria-hidden="true"
                />
                {required && !isDisabled && (
                  <span
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-destructive text-lg select-none"
                    title="Required"
                    aria-hidden="true"
                  >
                    *
                  </span>
                )}
              </div>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              {mode === 'range' ? (
                (() => {
                  let rangeSelected;
                  if (dateRange?.from && dateRange?.to) {
                    rangeSelected = {
                      from: safeParseDate(dateRange.from),
                      to: safeParseDate(dateRange.to),
                    };
                  } else if (dateRange?.from) {
                    rangeSelected = {
                      from: safeParseDate(dateRange.from),
                      to: undefined,
                    };
                  } else {
                    rangeSelected = undefined;
                  }

                  return (
                    <Calendar
                      mode="range"
                      selected={rangeSelected}
                      onSelect={handleRangeSelect}
                      numberOfMonths={numberOfMonths ?? 1}
                      disabled={disabledDays}
                      captionLayout="dropdown"
                      className="rounded-md border"
                      startMonth={new Date(2000, 0)}
                      endMonth={new Date(2099, 11)}
                      defaultMonth={
                        dateRange?.from
                          ? safeParseDate(dateRange.from)
                          : undefined
                      }
                      components={{
                        DayButton: BoundDayButton,
                      }}
                    />
                  );
                })()
              ) : (
                <Calendar
                  mode="single"
                  selected={date ? safeParseDate(date) : undefined}
                  onSelect={handleSelect}
                  numberOfMonths={numberOfMonths ?? 1}
                  disabled={disabledDays}
                  captionLayout="dropdown"
                  className="rounded-md border"
                  startMonth={new Date(2000, 0)}
                  endMonth={new Date(2099, 11)}
                  defaultMonth={date ? safeParseDate(date) : undefined}
                />
              )}
            </PopoverContent>
          </Popover>

          {enableTime && mode === 'single' && (
            <Popover open={isTimeOpen} onOpenChange={setIsTimeOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-[120px] justify-start text-left font-normal"
                  disabled={isDisabled}
                  aria-label="Time picker"
                  aria-expanded={isTimeOpen}
                  aria-haspopup="dialog"
                >
                  <Clock className="mr-2 h-4 w-4" />
                  {time ?? timePlaceholder}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <div className="grid grid-cols-2 gap-1 max-h-[200px] overflow-y-auto p-2">
                  {timeOptions.map((timeOption) => (
                    <button
                      key={timeOption}
                      type="button"
                      onClick={() => handleTimeChange(timeOption)}
                      className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                        time === timeOption
                          ? 'bg-accent text-accent-foreground font-medium'
                          : 'hover:bg-accent/50 hover:text-accent-foreground'
                      }`}
                    >
                      {timeOption}
                    </button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          )}
        </div>
      )}

      {enableTime && mode === 'range' && (
        <div
          className={`flex ${rangeLayout === 'side-by-side' ? 'flex-row' : 'flex-col'} gap-4`}
        >
          {/* FROM date and time row */}
          <div className="flex flex-col gap-2">
            {rangeFromLabel && (
              <Label className="text-sm font-semibold">{rangeFromLabel}</Label>
            )}
            <div className="flex gap-2 items-center">
              <Popover
                open={isRangeFromDateOpen}
                onOpenChange={setIsRangeFromDateOpen}
              >
                <PopoverTrigger asChild>
                  <div className="relative">
                    <input
                      type="text"
                      className={getRangeInputClasses(rangeFromDateValid)}
                      value={
                        rawRangeFromInput ||
                        (dateRange?.from
                          ? (() => {
                            const { date: datePart } = parseDateTimeString(
                              dateRange.from
                            );
                            return datePart ? formatDisplayDate(datePart) : '';
                          })()
                          : '')
                      }
                      onChange={handleRangeFromInputChange}
                      onBlur={handleRangeFromInputBlur}
                      placeholder={placeholder}
                      disabled={isDisabled}
                      aria-label="From date"
                    />
                    {showValidationError &&
                      !rangeFromDateValid &&
                      rangeFromDateError &&
                      !isDisabled && (
                        <p
                          className="text-destructive text-sm mt-1"
                          role="alert"
                          aria-live="polite"
                        >
                          {rangeFromDateError}
                        </p>
                    )}
                    <button
                      type="button"
                      onClick={() => setIsRangeFromDateOpen((prev) => !prev)}
                      disabled={isDisabled}
                      className={[
                        'absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground',
                        'hover:text-foreground disabled:opacity-50',
                      ].join(' ')}
                      aria-label="Open from date calendar"
                    >
                      <CalendarDays className="h-4 w-4" />
                    </button>
                  </div>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={
                      dateRange?.from
                        ? (() => {
                          const { date: datePart } = parseDateTimeString(
                            dateRange.from
                          );
                          return datePart
                            ? safeParseDate(datePart)
                            : undefined;
                        })()
                        : undefined
                    }
                    onSelect={handleSelectFromDate}
                    disabled={disabledDays}
                    captionLayout="dropdown"
                    className="rounded-md border"
                    startMonth={new Date(2000, 0)}
                    endMonth={new Date(2099, 11)}
                    defaultMonth={(() => {
                      if (rawRangeFromInput) {
                        const parsed = parseInputDate(rawRangeFromInput);
                        if (parsed) return parsed;
                      }
                      if (dateRange?.from) {
                        const { date: datePart } = parseDateTimeString(
                          dateRange.from
                        );
                        return datePart ? safeParseDate(datePart) : undefined;
                      }
                      return undefined;
                    })()}
                  />
                </PopoverContent>
              </Popover>

              <Popover
                open={isRangeFromTimeOpen}
                onOpenChange={setIsRangeFromTimeOpen}
              >
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-[120px] justify-start text-left font-normal"
                    disabled={isDisabled || !dateRange?.from}
                    aria-label="From time picker"
                    aria-expanded={isRangeFromTimeOpen}
                    aria-haspopup="dialog"
                  >
                    <Clock className="mr-2 h-4 w-4" />
                    {rangeTime.from ?? timePlaceholder}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <div className="grid grid-cols-2 gap-1 max-h-[200px] overflow-y-auto p-2">
                    {timeOptions.map((timeOption) => (
                      <button
                        key={timeOption}
                        type="button"
                        onClick={() => handleRangeFromTimeChange(timeOption)}
                        className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                          rangeTime.from === timeOption
                            ? 'bg-accent text-accent-foreground font-medium'
                            : 'hover:bg-accent/50 hover:text-accent-foreground'
                        }`}
                      >
                        {timeOption}
                      </button>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* TO date and time row */}
          <div className="flex flex-col gap-2">
            {rangeToLabel && (
              <Label className="text-sm font-semibold">{rangeToLabel}</Label>
            )}
            <div className="flex gap-2 items-center">
              <Popover
                open={isRangeToDateOpen}
                onOpenChange={setIsRangeToDateOpen}
              >
                <PopoverTrigger asChild>
                  <div className="relative">
                    <input
                      type="text"
                      className={getRangeInputClasses(rangeToDateValid)}
                      value={
                        rawRangeToInput ||
                        (dateRange?.to
                          ? (() => {
                            const { date: datePart } = parseDateTimeString(
                              dateRange.to
                            );
                            return datePart ? formatDisplayDate(datePart) : '';
                          })()
                          : '')
                      }
                      onChange={handleRangeToInputChange}
                      onBlur={handleRangeToInputBlur}
                      placeholder={placeholder}
                      disabled={isDisabled}
                      aria-label="To date"
                    />
                    {showValidationError &&
                      !rangeToDateValid &&
                      rangeToDateError &&
                      !isDisabled && (
                        <p
                          className="text-destructive text-sm mt-1"
                          role="alert"
                          aria-live="polite"
                        >
                          {rangeToDateError}
                        </p>
                    )}
                    <button
                      type="button"
                      onClick={() => setIsRangeToDateOpen((prev) => !prev)}
                      disabled={isDisabled}
                      className={[
                        'absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground',
                        'hover:text-foreground disabled:opacity-50',
                      ].join(' ')}
                      aria-label="Open to date calendar"
                    >
                      <CalendarDays className="h-4 w-4" />
                    </button>
                  </div>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={
                      dateRange?.to
                        ? (() => {
                          const { date: datePart } = parseDateTimeString(
                            dateRange.to
                          );
                          return datePart
                            ? safeParseDate(datePart)
                            : undefined;
                        })()
                        : undefined
                    }
                    onSelect={handleSelectToDate}
                    disabled={disabledDays}
                    captionLayout="dropdown"
                    className="rounded-md border"
                    startMonth={new Date(2000, 0)}
                    endMonth={new Date(2099, 11)}
                    defaultMonth={(() => {
                      if (rawRangeToInput) {
                        const parsed = parseInputDate(rawRangeToInput);
                        if (parsed) return parsed;
                      }
                      if (dateRange?.to) {
                        const { date: datePart } = parseDateTimeString(
                          dateRange.to
                        );
                        return datePart ? safeParseDate(datePart) : undefined;
                      }
                      return undefined;
                    })()}
                  />
                </PopoverContent>
              </Popover>

              <Popover
                open={isRangeToTimeOpen}
                onOpenChange={setIsRangeToTimeOpen}
              >
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-[120px] justify-start text-left font-normal"
                    disabled={isDisabled || !dateRange?.to}
                    aria-label="To time picker"
                    aria-expanded={isRangeToTimeOpen}
                    aria-haspopup="dialog"
                  >
                    <Clock className="mr-2 h-4 w-4" />
                    {rangeTime.to ?? timePlaceholder}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <div className="grid grid-cols-2 gap-1 max-h-[200px] overflow-y-auto p-2">
                    {timeOptions.map((timeOption) => (
                      <button
                        key={timeOption}
                        type="button"
                        onClick={() => handleRangeToTimeChange(timeOption)}
                        className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                          rangeTime.to === timeOption
                            ? 'bg-accent text-accent-foreground font-medium'
                            : 'hover:bg-accent/50 hover:text-accent-foreground'
                        }`}
                      >
                        {timeOption}
                      </button>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </div>
      )}

      {displayError && (
        <p
          id={`${id}-error`}
          className="text-destructive text-sm mt-1"
          role="alert"
          aria-live="polite"
        >
          {displayError}
        </p>
      )}
    </div>
  );
}

